import type { Client } from '@urql/vue';

import { getConfig } from '../config/constants';
import {
  type MmaLoggedQuery,
  MmaLoggedDocument,
  type MmaSessionFragmentFragment,
} from '../graphql/mma/generated/graphql';

import { useMmaBaseUserStatus } from '~/composables/useMmaBaseUserStatus';

interface UseSessionDataProps {
  onFulfilled?: (session: MmaSessionFragmentFragment) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRejected?: (reason: any) => void,
}

export const useSessionData = () => {
  const session = useMmaBaseUserStatus();

  const app = useNuxtApp();
  const urql = app.$urql as Client;

  const get = (props: UseSessionDataProps) => urql.query<MmaLoggedQuery>(MmaLoggedDocument, {}).then(
    (mmaBaseUser) => {
      session.value.fetching = false;
      session.value.data = mmaBaseUser.data?.mmaLogged;
      if (mmaBaseUser.data?.mmaLogged && props.onFulfilled) {
        props.onFulfilled(mmaBaseUser.data?.mmaLogged);
      } else if (props.onRejected) {
        props.onRejected(mmaBaseUser.error);
      }
      return mmaBaseUser;
    },
    (reason) => {
      session.value.fetching = false;
      session.value.error = reason;
      if (props.onRejected) {
        props.onRejected(reason);
      }
    },
  );

  return {
    get,
    session,
  };
};

const useLoadApp = () => {
  const config = getConfig(useRuntimeConfig());
  const cookie = useCsrfCookie(`${config.domains.api}/v2/sanctum/csrf-cookie`);
  const { session, get } = useSessionData();
  const getSessionData = (props: UseSessionDataProps = {}) => new Promise<MmaSessionFragmentFragment>(
    (resolve, reject) => {
      cookie.value.finally(() => get({
        onFulfilled: (mmaBaseUser) => {
          if (props.onFulfilled) {
            props.onFulfilled(mmaBaseUser);
          }
          resolve(mmaBaseUser);
        },
        onRejected: (reason) => {
          if (props.onRejected) {
            props.onRejected(reason);
          }
          reject(reason);
        },
      }));
    },
  );

  return {
    cookie,
    session,
    getSessionData,
  };
};

export default useLoadApp;
